import { Box, Card, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "../../../common/components/Button";
import { Logo } from "../../../common/components/Logo";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { forgotPassword } from "../../api/auth";
import styles from "../Login/Login.module.css";

export default function ForgotPassword() {
  const { t } = useTranslation("authentication");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!email) {
      setError(t("forgotPassword.errors.emailRequired"));
      setLoading(false);
      return;
    }

    const response = await forgotPassword(email);

    if (response.success) {
      setEmailSent(true);
      toast.success(t("forgotPassword.success"));
    } else {
      setError(response.message);
    }

    setLoading(false);
  };

  if (emailSent) {
    return (
      <div className={styles.loginPageWrapper}>
        {/* Animated background shapes */}
        <div className={styles.backgroundShapes}>
          <div className={`${styles.shape} ${styles.shape1}`}></div>
          <div className={`${styles.shape} ${styles.shape2}`}></div>
          <div className={`${styles.shape} ${styles.shape3}`}></div>
          <div className={`${styles.shape} ${styles.shape4}`}></div>
          <div className={`${styles.shape} ${styles.shape5}`}></div>
        </div>

        <Card
          style={{ maxWidth: 520 }}
          sx={{
            p: 4,
            boxShadow: 1,
            width: "100%",
            zIndex: 10,
            position: "relative",
          }}
        >
          <Logo style={{ maxWidth: 70 }} className="mb-3 mx-auto d-block" />

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <div style={{ fontSize: "48px", marginBottom: SPACING.sm }}>📧</div>
            <Typography
              variant="h4"
              sx={{ mb: SPACING.sm, fontWeight: 600, color: "success.main" }}
            >
              {t("forgotPassword.emailSent")}
            </Typography>
            <Typography
              variant="bodyMd"
              sx={{ mb: 3, color: "text.secondary" }}
            >
              {t("forgotPassword.sentLinkTo")} <strong>{email}</strong>
            </Typography>
            <Typography
              variant="bodySm"
              sx={{ mb: 3, color: "text.secondary" }}
            >
              {t("forgotPassword.checkEmail")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Link to="/login" className="text-decoration-none">
              <Button variant="outline" size="lg">
                {t("forgotPassword.backToLogin")}
              </Button>
            </Link>
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
              {t("forgotPassword.didntReceiveEmail")}{" "}
              <button
                type="button"
                onClick={() => {
                  setEmailSent(false);
                  setEmail("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  font: "inherit",
                }}
              >
                {t("forgotPassword.tryAgain")}
              </button>
            </Typography>
          </Box>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.loginPageWrapper}>
      {/* Animated background shapes */}
      <div className={styles.backgroundShapes}>
        <div className={`${styles.shape} ${styles.shape1}`}></div>
        <div className={`${styles.shape} ${styles.shape2}`}></div>
        <div className={`${styles.shape} ${styles.shape3}`}></div>
        <div className={`${styles.shape} ${styles.shape4}`}></div>
        <div className={`${styles.shape} ${styles.shape5}`}></div>
      </div>

      <Card
        style={{ maxWidth: 520 }}
        sx={{
          p: 4,
          boxShadow: 1,
          width: "100%",
          zIndex: 10,
          position: "relative",
        }}
      >
        <Logo style={{ maxWidth: 70 }} className="mb-3 mx-auto d-block" />

        <Box sx={{ textAlign: "center", mb: 4 }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔐</div>
          <Typography variant="h3" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
            {t("forgotPassword.title")}
          </Typography>
          <Typography variant="bodyMd" sx={{ color: "text.secondary" }}>
            {t("forgotPassword.description")}
          </Typography>
        </Box>

        {error && (
          <div className="mb-3">
            <span className="text-danger small">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="py-2">
            <TextField
              label={t("forgotPassword.emailLabel")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("forgotPassword.emailPlaceholder")}
              disabled={loading}
            />
          </div>

          <div className="py-2 mt-3">
            <Button variant="primary" size="lg" type="submit" loading={loading}>
              {t("forgotPassword.sendButton")}
            </Button>
          </div>
        </form>

        {/* Back to login link */}
        <div className="d-flex justify-content-center align-items-center py-4">
          <Typography variant="bodyMd" className="px-1 txt-lighter">
            {t("forgotPassword.rememberPassword")}
          </Typography>
          <Link to="/login" className="px-1 fw-500 text-decoration-none">
            <Typography variant="bodyMd" color="primary">
              {t("forgotPassword.backToLogin")}
            </Typography>
          </Link>
        </div>
      </Card>
    </div>
  );
}
